import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { AnimatePresence, motion, useMotionValue, useSpring } from "framer-motion";
import NavigationHeader from "@/components/NavigationHeader";
import Footer from "@/components/Footer";
import PageTransition from "@/components/PageTransition";
import { Button } from "@/components/ui/button";
import { ArrowRight, BookOpen, CheckCircle2, Eye, EyeOff, Loader2, Share2 } from "lucide-react";
import { getAllPosts } from "@/api/post";
import { getImageUrl } from "@/api/upload";
import { useNewsletterSubscribe } from "@/hooks/email/useNewsletterSubscribe";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import SEO from "@/components/SEO";
import { getCanonicalUrl, getSharePreviewUrlForPath } from "@/config/seo.config";
import axiosInstance from "@/config/axiosInstance";
import { useAuth } from "@/contexts/AuthContext";
import HomeAuroraBackground from "@/components/HomeAuroraBackground";
import TurnstileWidget from "@/components/shared/TurnstileWidget";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

// Assets
import ctaBanner from "@/assets/resources-cta-banner.webp";
import logoImg from "@/assets/logo.webp";
import appointmentVideo from "@/assets/appointment-video.mp4";
import mascotImg from "@/assets/mascot.gif";

const initialRegistrationForm = {
  fullName: "",
  email: "",
  clinicName: "",
  password: "",
};

const HERO_EASE = [0.16, 1, 0.3, 1];

const heroTextContainer = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.14,
      delayChildren: 0.05,
    },
  },
};

const heroTextItem = {
  hidden: { opacity: 0, y: 56, filter: "blur(10px)" },
  show: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: {
      duration: 0.9,
      ease: HERO_EASE,
    },
  },
};

const headerSegments = [
  { text: "Resources & Guides.", gradient: "text-[#1A3A5C]" },
  {
    text: "Insights that inspire innovation.",
    gradient:
      "bg-gradient-to-r from-[#9ecc48] via-teal-400 to-blue-500 bg-clip-text text-transparent",
  },
];

const BLOGS_PAGE_LIMIT = 6;

const Resources = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();

  // Scroll to section on mount if hash is present
  useEffect(() => {
    if (location.hash === "#latest-blogs") {
      setTimeout(() => {
        const element = document.getElementById("latest-blogs");
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 100);
    }
  }, [location]);

  // Component to conditionally show tooltip only when text is truncated
  const TextWithTooltip = ({ text, lineClamp = "line-clamp-2", className = "" }) => {
    const textRef = useRef(null);
    const [isTruncated, setIsTruncated] = useState(false);

    useEffect(() => {
      const checkTruncation = () => {
        const el = textRef.current;
        if (!el) return;
        // Compare natural scrollHeight against the clamped clientHeight
        // We temporarily remove the clamp class to measure the full height
        el.classList.remove(...lineClamp.split(" "));
        const fullHeight = el.scrollHeight;
        el.classList.add(...lineClamp.split(" "));
        setIsTruncated(fullHeight > el.clientHeight + 1);
      };

      checkTruncation();
      window.addEventListener("resize", checkTruncation);
      return () => window.removeEventListener("resize", checkTruncation);
    }, [text, lineClamp]);

    const textElement = (
      <div
        ref={textRef}
        className={`${lineClamp} ${className}`}
        style={{ cursor: isTruncated ? "help" : "default" }}>
        {text}
      </div>
    );

    if (!isTruncated) return textElement;

    return (
      <Tooltip>
        <TooltipTrigger asChild>{textElement}</TooltipTrigger>
        <TooltipContent side="top" className="max-w-md border-slate-700/60 bg-slate-900 text-slate-100">
          <p>{text}</p>
        </TooltipContent>
      </Tooltip>
    );
  };

  const [email, setEmail] = useState("");
  const [blogPosts, setBlogPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [currentBlogsPage, setCurrentBlogsPage] = useState(1);
  const [hasMoreBlogs, setHasMoreBlogs] = useState(false);

  // --- VIDEO STATE ---
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authModalStep, setAuthModalStep] = useState("question");
  const [registrationForm, setRegistrationForm] = useState(initialRegistrationForm);
  const [registrationError, setRegistrationError] = useState("");
  const [isRegisterSubmitting, setIsRegisterSubmitting] = useState(false);
  const [registrationSuccessMessage, setRegistrationSuccessMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);
  const isTurnstileEnabled = Boolean(import.meta.env.VITE_TURNSTILE_SITE_KEY);
  const turnstileRef = useRef(null);

  // Custom Cursor Setup
  const springConfig = { damping: 30, stiffness: 250, mass: 0.8 };
  const cursorX = useMotionValue(-100);
  const cursorY = useMotionValue(-100);
  const _cursorXSpring = useSpring(cursorX, springConfig);
  const _cursorYSpring = useSpring(cursorY, springConfig);

  // --- MODAL SIDE EFFECTS (Hide Nav & Widgets) ---
  useEffect(() => {
    if (isAuthModalOpen) {
      // Add a class to the body in case you have global CSS hiding the nav
      document.body.classList.add("demo-modal-open");
    } else {
      document.body.classList.remove("demo-modal-open");
    }

    // Cleanup function
    return () => document.body.classList.remove("demo-modal-open");
  }, [isAuthModalOpen]);

  // Cleaned up useEffect: Only tracks custom cursor now, mascot tracking removed
  useEffect(() => {
    const handleMouseMove = (e) => {
      cursorX.set(e.clientX);
      cursorY.set(e.clientY);
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, [cursorX, cursorY]);

  const toggleVideoPlay = async (e) => {
    e?.stopPropagation();

    if (!videoRef.current) return;

    const hasVideoAccess = Boolean(isAuthenticated && user);
    if (!hasVideoAccess) {
      if (!videoRef.current.paused) {
        videoRef.current.pause();
      }
      setIsPlaying(false);
      setRegistrationError("");
      setRegistrationSuccessMessage("");
      setAuthModalStep("question");
      setIsAuthModalOpen(true);
      return;
    }

    if (!videoRef.current.paused) {
      videoRef.current.pause();
      setIsPlaying(false);
      return;
    }

    try {
      await videoRef.current.play();
      setIsPlaying(true);
    } catch (error) {
      console.error("Video playback failed:", error);
      setIsPlaying(false);
    }
  };

  useEffect(() => {
    if (!isAuthModalOpen || !videoRef.current) return;

    videoRef.current.pause();
    setIsPlaying(false);
  }, [isAuthModalOpen]);

  const handleAuthModalOpenChange = (open) => {
    setIsAuthModalOpen(open);
    if (open) return;

    setAuthModalStep("question");
    setRegistrationError("");
    setIsRegisterSubmitting(false);
  };

  const handleGoToLogin = () => {
    handleAuthModalOpenChange(false);
    navigate("/login");
  };

  const handleRegistrationChange = (e) => {
    const { name, value } = e.target;
    setRegistrationForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleRegistrationSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      name: registrationForm.fullName.trim(),
      email: registrationForm.email.trim().toLowerCase(),
      clinicName: registrationForm.clinicName.trim(),
      password: registrationForm.password,
    };

    if (!payload.name || !payload.email || !payload.clinicName || !payload.password) {
      setRegistrationError("Please complete all required fields.");
      return;
    }

    setRegistrationError("");
    setIsRegisterSubmitting(true);

    try {
      const response = await axiosInstance.post("/api/auth/register", payload, {
        withCredentials: true,
      });

      setRegistrationSuccessMessage(
        response?.data?.message ||
          "Account request submitted. Your Vet Assist account is pending administrator approval."
      );
      setRegistrationForm(initialRegistrationForm);
      setAuthModalStep("success");
    } catch (error) {
      const statusCode = error.response?.status;
      const errorCode = error.response?.data?.errorCode;
      const message = error.response?.data?.message;

      if (statusCode === 429) {
        setRegistrationError("Too many attempts. Please wait a moment, then try again.");
        return;
      }

      if (statusCode === 409 || errorCode === "EMAIL_EXISTS") {
        setRegistrationError("This email already exists. Please sign in instead.");
        return;
      }

      if (statusCode === 400 || errorCode === "VALIDATION_ERROR") {
        setRegistrationError(message || "Please review your details and try again.");
        return;
      }

      if (statusCode === 500) {
        setRegistrationError("Server error. Please try again shortly.");
        return;
      }

      setRegistrationError(message || "Unable to submit registration right now.");
    } finally {
      setIsRegisterSubmitting(false);
    }
  };

  const { mutate: subscribeNewsletter, isPending } = useNewsletterSubscribe();

  const formatPosts = (posts = []) => {
    return posts.map((post) => ({
      id: post.id,
      title: post.title,
      date: new Date(post.published_at || post.created_at).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      }),
      category: post.category_name || "Tutorial",
      image: post.featured_image ? getImageUrl(post.featured_image) : logoImg,
      slug: post.slug,
      excerpt: post.excerpt || "",
    }));
  };

  // --- FETCH BLOG POSTS ---
  useEffect(() => {
    const fetchPosts = async () => {
      try {
        setIsLoading(true);
        const response = await getAllPosts({
          isPublished: true,
          page: 1,
          limit: BLOGS_PAGE_LIMIT,
        });

        const posts = Array.isArray(response?.data) ? response.data : [];
        const pageFromResponse = Number(response?.pagination?.page || 1);
        const totalPages = Number(response?.pagination?.totalPages || 1);

        setBlogPosts(formatPosts(posts));
        setCurrentBlogsPage(pageFromResponse);
        setHasMoreBlogs(pageFromResponse < totalPages);
      } catch (error) {
        console.error("Failed to fetch blog posts:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPosts();
  }, []);

  const handleShowMoreBlogs = async () => {
    if (isLoadingMore || !hasMoreBlogs) return;

    try {
      setIsLoadingMore(true);
      const nextPage = currentBlogsPage + 1;
      const response = await getAllPosts({
        isPublished: true,
        page: nextPage,
        limit: BLOGS_PAGE_LIMIT,
      });

      const posts = Array.isArray(response?.data) ? response.data : [];
      const pageFromResponse = Number(response?.pagination?.page || nextPage);
      const totalPages = Number(response?.pagination?.totalPages || pageFromResponse);
      const formattedPosts = formatPosts(posts);

      setBlogPosts((prev) => {
        const nextItems = formattedPosts.filter(
          (item) => !prev.some((existing) => existing.id === item.id)
        );
        return [...prev, ...nextItems];
      });
      setCurrentBlogsPage(pageFromResponse);
      setHasMoreBlogs(pageFromResponse < totalPages);
    } catch (error) {
      console.error("Failed to load more blog posts:", error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    const normalizedEmail = email.trim();
    if (!normalizedEmail) return;

    if (isTurnstileEnabled && !turnstileToken) {
      toast.error("Captcha verification failed. Please try again.");
      return;
    }

    subscribeNewsletter(
      {
        email: normalizedEmail,
        source: "Resources page CTA",
        turnstileToken,
      },
      {
        onSuccess: () => {
          setEmail("");
          setTurnstileToken("");
          setTurnstileResetKey((prev) => prev + 1);
          turnstileRef.current?.reset?.();
        },
      }
    );
  };

  const handleShare = async (e, post) => {
    e.preventDefault();
    e.stopPropagation();

    const canonicalUrl = getCanonicalUrl(`/resources/${post.slug}`);
    const shareUrl = getSharePreviewUrlForPath(`/resources/${post.slug}`);
    const shareData = {
      title: post.title,
      text: post.excerpt || "Check out this blog post",
      url: shareUrl,
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        if (err.name !== "AbortError") console.error("Error sharing:", err);
      }
    } else {
      try {
        await navigator.clipboard.writeText(shareUrl || canonicalUrl);
        alert("Share link copied to clipboard!");
      } catch (err) {
        console.error("Failed to copy:", err);
      }
    }
  };

  return (
    <PageTransition>
      <div
        className="relative w-full overflow-hidden font-sans text-slate-900"
        style={{
          background: "linear-gradient(135deg, #dbeafe 0%, #e8f4ff 20%, #f0fdf4 60%, #dcfce7 100%)",
        }}>
        <SEO page="resources" />

        {/* Hide navigation when the auth modal is open */}
        <div className={isAuthModalOpen ? "pointer-events-none hidden opacity-0" : "block"}>
          <NavigationHeader />
        </div>

        {/* --- BACKGROUND ANIMATIONS --- */}
        <style>{`
        @keyframes aura-rotate {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        .animate-aura {
          background-size: 300% 300%;
          animation: aura-rotate 6s ease infinite;
        }
      `}</style>

        <HomeAuroraBackground
          showStars={false}
          showWavingLines={true}
          className="pointer-events-none fixed inset-0 z-0 overflow-hidden"
        />

        <div className="relative z-10">
          {/* =========================================================
            HERO SECTION (AURA BORDER & INLINE VIDEO)
           ========================================================= */}
          <section className="relative px-0 pt-36 pb-16">
            <div className="relative z-10 px-8 lg:px-20">
              {/* HERO TEXT */}
              <motion.div
                variants={heroTextContainer}
                initial="hidden"
                animate="show"
                className="mx-auto mb-20 flex max-w-300 flex-col items-start justify-center gap-4 text-left">
                <motion.span
                  variants={heroTextItem}
                  className="mb-6 inline-flex items-center gap-2 rounded-full border border-emerald-500/20 bg-emerald-500/10 px-5 py-2 text-sm font-medium text-emerald-800 shadow-sm backdrop-blur-sm transition-colors hover:bg-emerald-500/20">
                  <BookOpen className="h-4 w-4 text-emerald-500" />
                  Learning
                </motion.span>
                <motion.h1
                  variants={heroTextItem}
                  style={{ perspective: "1000px" }}
                  className="flex flex-col gap-0 text-5xl leading-[1.1] font-bold tracking-tighter md:text-7xl lg:text-[5rem]">
                  {headerSegments.map((segment, index) => (
                    <div key={index} className="-mb-8 overflow-hidden pb-8">
                      <motion.span
                        variants={heroTextItem}
                        className={`block pb-2 ${segment.gradient}`}>
                        {segment.text}
                      </motion.span>
                    </div>
                  ))}
                </motion.h1>
              </motion.div>

              {/* VIDEO CONTAINER WITH GLOWING BORDER */}
              <div className="relative mx-auto w-full max-w-337.5 rounded-3xl p-0.5">
                {/* Outer Glowing Aura */}
                <div className="animate-aura absolute -inset-2.5 rounded-[1.2rem] bg-linear-to-r from-blue-600 via-cyan-400 to-purple-600 opacity-60 mix-blend-screen blur-xl" />
                <div className="animate-aura absolute -inset-0.5 rounded-[1.1rem] bg-linear-to-r from-blue-500 via-emerald-400 to-purple-500" />

                {/* Animated Floating Mascot Image (Enlarged and shifted right) */}
                <motion.div
                  animate={{ y: [0, -20, 0] }}
                  transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                  className="pointer-events-none absolute -top-12 -right-4 z-50 h-28 w-28 sm:-top-16 sm:-right-10 sm:h-36 sm:w-36 md:-top-20 md:-right-12 md:h-44 md:w-44 lg:-top-24 lg:-right-16 lg:h-52 lg:w-52 xl:-top-28 xl:-right-24 xl:h-64 xl:w-64">
                  <div className="relative h-full w-full">
                    <img
                      src={mascotImg}
                      alt="Mascot"
                      className="h-full w-full object-contain"
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                  </div>
                </motion.div>

                {/* Inner Browser Window */}
                <div className="relative z-10 flex flex-col overflow-hidden rounded-3xl border border-slate-700/50 bg-[#0d1117] shadow-2xl">
                  {/* Mockup Top Bar (MacOS dots) */}
                  <div className="flex h-12 w-full items-center gap-2 border-b border-white/5 bg-[#161b22] px-4">
                    <div className="h-3.5 w-3.5 rounded-full bg-[#ff5f56] shadow-inner" />
                    <div className="h-3.5 w-3.5 rounded-full bg-[#ffbd2e] shadow-inner" />
                    <div className="h-3.5 w-3.5 rounded-full bg-[#27c93f] shadow-inner" />
                  </div>

                  {/* Inline Video Player */}
                  <div className="group relative aspect-video bg-black">
                    <video
                      ref={videoRef}
                      className="block h-full w-full object-cover"
                      src={appointmentVideo}
                      playsInline
                      loop
                      onPlay={() => setIsPlaying(true)}
                      onPause={() => setIsPlaying(false)}
                    />

                    {/* Play/Pause Overlay Component */}
                    <div
                      className={`pointer-events-none absolute inset-0 bg-black/40 transition-opacity duration-300 ${
                        isPlaying ? "opacity-0 group-hover:opacity-100" : "opacity-100"
                      }`}>
                      <button
                        onClick={toggleVideoPlay}
                        className="pointer-events-auto absolute right-6 bottom-6 flex h-12 w-12 cursor-pointer items-center justify-center rounded-full border border-white/20 bg-white/10 text-white shadow-lg backdrop-blur-md transition-all duration-300 hover:scale-110">
                        {isPlaying ? (
                          <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
                          </svg>
                        ) : (
                          <svg
                            className="h-5 w-5" /* <-- Removed translate-x-0.5 here */
                            fill="currentColor"
                            viewBox="0 0 24 24">
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* =========================================================
            LATEST BLOGS SECTION
           ========================================================= */}
          <section id="latest-blogs" className="relative px-6 py-12 md:px-12 md:py-16">
            <div className="absolute top-0 right-0 left-0 h-px bg-linear-to-r from-transparent via-white/10 to-transparent" />

            <div className="mx-auto w-full max-w-400">
              {/* Title Section */}
              <div className="relative z-10 mx-auto mb-20 flex max-w-4xl flex-col items-center text-center">
                <span className="mb-6 inline-flex cursor-pointer items-center gap-2 rounded-full border border-blue-500/30 bg-linear-to-r from-blue-500/20 to-[#9ecc48]/20 px-5 py-2 text-sm font-medium text-emerald-800 shadow-lg shadow-blue-500/10 backdrop-blur-sm transition-shadow hover:shadow-md">
                  <BookOpen size={16} className="animate-pulse text-blue-400" />
                  OUR BLOG
                  <span
                    className="h-1.5 w-1.5 rounded-full bg-blue-400"
                    style={{ animation: "pulse-glow 2s ease-in-out infinite" }}
                  />
                </span>

                <h1 className="relative mb-6 inline-block text-[clamp(3rem,5vw,4.5rem)] leading-[1.1] font-bold tracking-tight text-[#1A3A5C]">
                  Latest{" "}
                  <span className="relative">
                    <span className="animate-gradient-x bg-linear-to-r from-blue-400 via-blue-300 to-[#9ecc48] bg-clip-text text-transparent">
                      Insights & News
                    </span>
                    <span className="absolute -bottom-2 left-0 h-1 w-full rounded-full bg-linear-to-r from-blue-500 to-[#9ecc48] opacity-50" />
                  </span>
                </h1>

                <p className="mx-auto max-w-2xl text-xl leading-relaxed font-light text-slate-700">
                  Discover strategies, stories, and technical updates from the Endsofttech team to
                  help your business grow.
                </p>
              </div>

              {/* Blogs Grid - Loading State */}
              {isLoading && (
                <div className="mb-12 grid grid-cols-1 gap-x-8 gap-y-12 md:grid-cols-2 lg:grid-cols-3">
                  {[1, 2, 3].map((i) => (
                    <div
                      key={i}
                      className="h-96 animate-pulse rounded-[2rem] bg-slate-800/50 backdrop-blur-sm"
                    />
                  ))}
                </div>
              )}

              {/* Blogs Grid - Empty State */}
              {!isLoading && blogPosts.length === 0 && (
                <div className="py-20 text-center">
                  <p className="text-lg text-[#5A7FA8]">
                    Fresh insights are on the way. Please check back soon.
                  </p>
                </div>
              )}

              {/* Blogs Grid */}
              {!isLoading && blogPosts.length > 0 && (
                <>
                  <div className="mb-12 grid grid-cols-1 gap-x-8 gap-y-12 md:grid-cols-2 lg:grid-cols-3">
                    {blogPosts.map((post) => (
                      <Link
                        to={`/resources/${post.slug}`}
                        key={post.id}
                        className="group relative flex h-full cursor-pointer flex-col overflow-hidden rounded-[2rem] border border-[#93c5fd]/30 bg-white/70 shadow-2xl backdrop-blur-xl transition-all duration-500 ease-out hover:-translate-y-2 hover:border-[#93c5fd]/50 hover:shadow-[0_20px_40px_-15px_rgba(37,70,161,0.25)]">
                        <div className="relative h-72 overflow-hidden md:h-80">
                          <div className="absolute inset-0 z-10 bg-slate-900/5 transition-colors duration-500 group-hover:bg-slate-900/0" />
                          <img
                            src={post.image}
                            alt={post.title}
                            className="h-full w-full transform object-cover object-top transition-transform duration-700 ease-in-out"
                          />
                          <div className="absolute top-4 right-4 z-20">
                            <div className="rounded-full border border-[#93c5fd]/35 bg-white/85 px-4 py-1.5 shadow-sm backdrop-blur-md">
                              <span className="text-[11px] font-bold tracking-widest text-[#1A3A5C] uppercase">
                                {post.category}
                              </span>
                            </div>
                          </div>
                          <button
                            onClick={(e) => handleShare(e, post)}
                            className="absolute right-4 bottom-4 z-20 flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border border-[#93c5fd]/35 bg-white/85 text-[#1A3A5C] opacity-0 shadow-lg backdrop-blur-md transition-all duration-300 group-hover:opacity-100 hover:bg-[#295CAC]/15 hover:text-[#1A3A5C]"
                            title="Share this post"
                            aria-label="Share this post">
                            <Share2 className="h-4 w-4" />
                          </button>
                        </div>

                        <div className="relative flex grow flex-col p-8">
                          <TextWithTooltip
                            text={post.title}
                            lineClamp="line-clamp-2"
                            className="mb-4 text-xl leading-tight font-bold text-[#1A3A5C] transition-colors duration-300 group-hover:text-[#9ecc48]"
                          />
                          {post.excerpt && (
                            <TextWithTooltip
                              text={post.excerpt}
                              lineClamp="line-clamp-2"
                              className="mb-4 text-base leading-relaxed text-slate-400"
                            />
                          )}
                          <p className="mb-8 grow text-sm leading-relaxed font-light text-slate-500">
                            {post.date}
                          </p>
                          <div className="mt-auto border-t border-[#93c5fd]/25 pt-6">
                            <div className="flex items-center justify-between text-sm font-bold text-[#1A3A5C] transition-colors group-hover:text-[#9ecc48]">
                              <span>READ ARTICLE</span>
                              <span className="flex h-8 w-8 items-center justify-center rounded-full border border-[#93c5fd]/25 bg-white/80 transition-all duration-300 group-hover:border-[#9ecc48]/50 group-hover:bg-[#9ecc48]/20 group-hover:shadow-[0_0_10px_rgba(158,204,72,0.3)]">
                                <ArrowRight
                                  size={14}
                                  className="transform text-[#5A7FA8] transition-transform duration-300 group-hover:translate-x-1 group-hover:text-[#9ecc48]"
                                />
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="h-1 w-full bg-linear-to-r from-blue-400 to-[#9ecc48] opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                      </Link>
                    ))}
                  </div>

                  {hasMoreBlogs && (
                    <div className="mb-12 flex justify-center">
                      <Button
                        type="button"
                        onClick={handleShowMoreBlogs}
                        disabled={isLoadingMore}
                        className="cursor-pointer rounded-full border border-[#295CAC]/30 bg-transparent px-8 py-6 text-sm font-medium text-[#295CAC] shadow-none transition-colors duration-300 hover:bg-[#295CAC]/10 disabled:cursor-not-allowed disabled:opacity-60">
                        {isLoadingMore ? "Loading..." : "Show More"}
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          </section>

          {/* =========================================================
            NEWSLETTER SECTION
           ========================================================= */}
          <section
            className="relative overflow-hidden bg-cover bg-center bg-no-repeat px-6 py-16 md:px-12"
            style={{
              backgroundImage: `url(${ctaBanner})`,
              backgroundSize: "100% 100%",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
            }}>
            <div className="absolute inset-0 bg-slate-900/60" />
            <div className="absolute inset-0 bg-linear-to-r from-transparent via-slate-950/40 to-slate-950/95" />

            <div className="relative z-10 mx-auto flex w-full max-w-400 items-center justify-end">
              <div className="w-full text-left lg:w-1/2 lg:pl-12">
                <h2 className="mb-6 text-4xl leading-tight font-bold text-white drop-shadow-md md:text-5xl lg:text-6xl">
                  Want to stay ahead in the{" "}
                  <span className="bg-linear-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent drop-shadow-lg filter">
                    veterinary world?
                  </span>
                </h2>

                <p className="mb-10 text-lg leading-relaxed font-medium text-slate-300 md:text-xl">
                  Subscribe for the latest news, trends, and expert
                  <br className="hidden lg:block" /> advice delivered straight to your inbox.
                </p>

                <form onSubmit={handleSubscribe} className="flex max-w-xl flex-col gap-4">
                  <div className="flex w-full flex-col gap-3 sm:flex-row">
                    <div className="flex-1">
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Enter your email"
                        className="w-full rounded-xl border border-white/10 bg-slate-900/50 px-6 py-4 text-base text-white placeholder-slate-400 shadow-lg backdrop-blur-md transition-all focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 focus:outline-none"
                        required
                        disabled={isPending}
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={isPending || (isTurnstileEnabled && !turnstileToken)}
                      className="h-auto w-full cursor-pointer rounded-xl bg-[#10B981] px-8 py-4 text-base font-bold text-white shadow-[0_0_20px_rgba(16,185,129,0.3)] transition-all duration-300 hover:scale-105 hover:bg-[#059669] sm:w-auto">
                      {isPending ? "Subscribing..." : "Subscribe"}
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </div>

                  {isTurnstileEnabled && (
                    <div className="flex flex-col items-start gap-3">
                      <TurnstileWidget
                        ref={turnstileRef}
                        className="w-full text-left"
                        action="newsletter_subscribe"
                        mode="managed"
                        resetKey={turnstileResetKey}
                        onTokenChange={setTurnstileToken}
                        onError={(message) => toast.error(message)}
                      />
                    </div>
                  )}
                </form>
              </div>
            </div>
          </section>

          <Footer />
        </div>

        <Dialog open={isAuthModalOpen} onOpenChange={handleAuthModalOpenChange}>
          {/* Added !bg-slate-950 and !text-white to forcefully override Shadcn's default light mode classes */}
          <DialogContent className="w-[95vw] max-w-xl overflow-hidden border border-slate-700/80 bg-slate-950! p-0 text-white!">
            {/* Added a solid bg-slate-950 base here to ensure the background is dark under the gradient */}
            <div className="relative bg-slate-950">
              <div className="pointer-events-none absolute inset-0 bg-linear-to-br from-blue-500/20 via-emerald-500/10 to-transparent" />
              <div className="relative p-7 sm:p-8">
                <DialogHeader className="space-y-2 text-left">
                  <DialogTitle className="text-2xl font-semibold text-white">
                    Unlock Vet Assist Video
                  </DialogTitle>
                  <DialogDescription className="text-sm text-slate-300">
                    {authModalStep === "question" && "Do you already have a Vet Assist account?"}
                    {authModalStep === "register" &&
                      "Create your clinic account request. Admin approval is required before full access."}
                    {authModalStep === "success" && "Registration request submitted."}
                  </DialogDescription>
                </DialogHeader>

                <AnimatePresence mode="wait" initial={false}>
                  {authModalStep === "question" && (
                    <motion.div
                      key="question"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      transition={{ duration: 0.2 }}
                      className="mt-7 space-y-3">
                      <Button
                        type="button"
                        onClick={handleGoToLogin}
                        className="h-11 w-full cursor-pointer rounded-xl bg-emerald-500 font-semibold text-slate-950 hover:bg-emerald-400">
                        Yes, I have an account
                      </Button>
                      <Button
                        type="button"
                        onClick={() => {
                          setRegistrationError("");
                          setAuthModalStep("register");
                        }}
                        variant="outline"
                        className="h-11 w-full cursor-pointer rounded-xl border-slate-600 bg-transparent font-semibold text-slate-100 hover:bg-slate-800">
                        No, create a new account
                      </Button>
                    </motion.div>
                  )}

                  {authModalStep === "register" && (
                    <motion.form
                      key="register"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      transition={{ duration: 0.2 }}
                      onSubmit={handleRegistrationSubmit}
                      className="mt-7 space-y-4">
                      <div>
                        <label className="mb-1 block text-sm font-medium text-slate-200">
                          Full Name
                        </label>
                        <input
                          type="text"
                          name="fullName"
                          value={registrationForm.fullName}
                          onChange={handleRegistrationChange}
                          autoComplete="name"
                          className="h-11 w-full rounded-xl border border-slate-600 bg-slate-900/70 px-4 text-sm text-white placeholder:text-slate-400 focus:border-emerald-400 focus:outline-none"
                          placeholder="Jane Doe"
                          required
                        />
                      </div>

                      <div>
                        <label className="mb-1 block text-sm font-medium text-slate-200">
                          Email
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={registrationForm.email}
                          onChange={handleRegistrationChange}
                          autoComplete="email"
                          className="h-11 w-full rounded-xl border border-slate-600 bg-slate-900/70 px-4 text-sm text-white placeholder:text-slate-400 focus:border-emerald-400 focus:outline-none"
                          placeholder="you@clinic.com"
                          required
                        />
                      </div>

                      <div>
                        <label className="mb-1 block text-sm font-medium text-slate-200">
                          Clinic Name
                        </label>
                        <input
                          type="text"
                          name="clinicName"
                          value={registrationForm.clinicName}
                          onChange={handleRegistrationChange}
                          className="h-11 w-full rounded-xl border border-slate-600 bg-slate-900/70 px-4 text-sm text-white placeholder:text-slate-400 focus:border-emerald-400 focus:outline-none"
                          placeholder="Vet Assist Clinic"
                          required
                        />
                      </div>

                      <div>
                        <label className="mb-1 block text-sm font-medium text-slate-200">
                          Password
                        </label>
                        <div className="relative">
                          <input
                            type={showPassword ? "text" : "password"}
                            name="password"
                            value={registrationForm.password}
                            onChange={handleRegistrationChange}
                            autoComplete="new-password"
                            className="h-11 w-full rounded-xl border border-slate-600 bg-slate-900/70 px-4 text-sm text-white placeholder:text-slate-400 focus:border-emerald-400 focus:outline-none"
                            placeholder="Create a secure password"
                            required
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute inset-y-0 right-0 flex cursor-pointer items-center px-3 text-slate-400 hover:text-white">
                            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                          </button>
                        </div>
                      </div>

                      {registrationError && (
                        <p className="rounded-lg border border-red-500/50 bg-red-500/10 px-3 py-2 text-sm text-red-200">
                          {registrationError}
                        </p>
                      )}

                      <div className="flex flex-col gap-3 pt-1 sm:flex-row">
                        <Button
                          type="button"
                          variant="outline"
                          disabled={isRegisterSubmitting}
                          onClick={() => {
                            setRegistrationError("");
                            setAuthModalStep("question");
                          }}
                          className="h-11 flex-1 cursor-pointer rounded-xl border-slate-600 bg-transparent text-slate-100 hover:bg-slate-800">
                          Back
                        </Button>
                        <Button
                          type="submit"
                          disabled={isRegisterSubmitting}
                          className="h-11 flex-1 cursor-pointer rounded-xl bg-emerald-500 font-semibold text-slate-950 hover:bg-emerald-400">
                          {isRegisterSubmitting ? (
                            <span className="inline-flex items-center gap-2">
                              Submitting
                              <Loader2 className="h-4 w-4 animate-spin" />
                            </span>
                          ) : (
                            "Submit for Approval"
                          )}
                        </Button>
                      </div>
                    </motion.form>
                  )}

                  {authModalStep === "success" && (
                    <motion.div
                      key="success"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      transition={{ duration: 0.2 }}
                      className="mt-7 space-y-5">
                      <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/10 p-4 text-sm leading-relaxed text-emerald-100">
                        <div className="mb-2 flex items-center gap-2 text-emerald-200">
                          <CheckCircle2 className="h-5 w-5" />
                          Request Received
                        </div>
                        <p>
                          {registrationSuccessMessage ||
                            "Your account request is pending administrator approval."}
                        </p>
                      </div>

                      <div className="flex flex-col gap-3 sm:flex-row">
                        <Button
                          type="button"
                          onClick={handleGoToLogin}
                          className="h-11 flex-1 cursor-pointer rounded-xl bg-emerald-500 font-semibold text-slate-950 hover:bg-emerald-400">
                          Go to Login
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => handleAuthModalOpenChange(false)}
                          className="h-11 flex-1 cursor-pointer rounded-xl border-slate-600 bg-transparent text-slate-100 hover:bg-slate-800">
                          Close
                        </Button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </PageTransition>
  );
};

export default Resources;
